export const CAPTIONS_STYLE_PRESET_NAMES = Object.seal([
  'Impactful',
  'Dynamic',
  'Classic',
  'Popping',
  'Professional',
] as const);

export type CaptionsStylePresetName = (typeof CAPTIONS_STYLE_PRESET_NAMES)[number];

type CssValue = string | number;

export type CaptionsStyleDefinition = {
  grouping?: {
    words?: number;
    characters?: number;
    minDurationInSeconds?: number;
  } | null;

  align?: 'left' | 'center' | 'right';

  animation?: 'pop' | 'fade-in' | 'fade-in-out' | 'none';

  padding?: CssValue;

  fontSize?: number;
  fontWeight?: CssValue;
  fontFamily?: string;
  lineHeight?: CssValue;
  color?: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  textTransform?: 'uppercase' | 'lowercase' | 'capitalize';
  textStroke?: {
    color?: string;
    width?: number;
  } | null;
  textShadow?: {
    color?: string;
    x?: number;
    y?: number;
    blur?: number;
    strength?: number;
  } | null;

  box?: {
    type?: 'group' | 'line' | 'sliding';
    backgroundColor?: string;
    padding?: CssValue;
    borderRadius?: number;
  } | null;

  spokenWord?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    padding?: CssValue;
    margin?: CssValue;
    borderRadius?: number;
  } | null;

  currentWord?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    padding?: CssValue;
    margin?: CssValue;
    borderRadius?: number;
  } | null;
};

const Impactful = Object.seal({
  grouping: { words: 8, characters: 40 },
  fontSize: 64,
  fontWeight: 900,
  fontFamily: 'Montserrat, sans-serif',
  lineHeight: 1.25,
  color: 'white',
  textAlign: 'center',
  textTransform: 'uppercase',
  textStroke: {
    color: 'black',
    width: 8,
  },
  textShadow: {
    color: 'black',
    x: 4,
    y: 4,
    blur: 12,
    strength: 8,
  },

  currentWord: {
    fontSize: 64,
    color: 'mediumpurple',
  },
}) satisfies CaptionsStyleDefinition;

const Dynamic = Object.seal({
  grouping: { words: 8, characters: 40 },

  fontSize: 80,
  fontWeight: 900,
  fontFamily: 'Roboto Condensed, sans-serif',
  lineHeight: 1.5,
  color: 'white',
  textAlign: 'center',
  textStroke: {
    color: 'black',
    width: 4,
  },
  textShadow: {
    color: 'black',
    x: 5,
    y: 5,
    blur: 30,
    strength: 1,
  },

  currentWord: {
    backgroundColor: 'mediumpurple',
    padding: 16,
    margin: -16,
    borderRadius: 16,
  },
}) satisfies CaptionsStyleDefinition;

const Classic = Object.seal({
  grouping: { words: 16, characters: 70 },
  fontSize: 48,
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  lineHeight: 2,
  color: 'grey',

  box: {
    type: 'line',
    backgroundColor: 'black',
    padding: 14,
    borderRadius: 10,
  },

  spokenWord: {
    color: 'white',
  },

  currentWord: {
    color: 'white',
  },
}) satisfies CaptionsStyleDefinition;

const Professional = Object.seal({
  grouping: { words: 20, characters: 75 },
  fontSize: 48,
  fontWeight: 600,
  fontFamily: 'Open Sans, sans-serif',
  lineHeight: 1.75,
  color: 'white',

  box: {
    type: 'group',
    backgroundColor: 'black',
    padding: 20,
    borderRadius: 12,
  },
}) satisfies CaptionsStyleDefinition;

const Popping = Object.seal({
  grouping: { words: 1, minDurationInSeconds: 0.25 },
  fontSize: 120,
  fontWeight: 800,
  fontFamily: 'Poppins, sans-serif',
  lineHeight: 1.25,
  color: 'white',
  textAlign: 'center',
  textStroke: {
    color: 'black',
    width: 16,
  },

  animation: 'pop',
}) satisfies CaptionsStyleDefinition;

export const mergeCaptionsStyle = (preset?: CaptionsStylePresetName, custom?: CaptionsStyleDefinition | null) => {
  const base = CAPTIONS_STYLE_PRESETS[preset ?? DEFAULT_CAPTIONS_STYLE_PRESET_NAME];

  if (!custom) {
    return base;
  }

  return {
    ...base,
    ...Object.fromEntries(
      Object.entries(custom)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => (v === null ? [k, undefined] : [k, v]))
    ),
    ...(
      ['grouping', 'textStroke', 'textShadow', 'box', 'spokenWord', 'currentWord'] as const
    ).reduce<CaptionsStyleDefinition>((sub, key) => {
      if (custom[key] === null) {
        return sub;
      }

      if (!custom[key] && !base[key]) {
        return sub;
      }

      return {
        ...sub,
        [key]: {
          ...base[key],
          ...custom[key],
        },
      };
    }, {}),
  };
};

export const CAPTIONS_STYLE_PRESETS: Readonly<Record<CaptionsStylePresetName, Readonly<CaptionsStyleDefinition>>> =
  Object.seal({
    Impactful,
    Dynamic,
    Classic,
    Popping,
    Professional,
  });

export const DEFAULT_CAPTIONS_STYLE_PRESET_NAME: CaptionsStylePresetName = 'Impactful';
